import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiMoreVertical, FiSearch, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import {
  createUser,
  deleteUser,
  fetchAllRoles,
  fetchUsersForUsersPage,
  humanizeError,
  normalizeUserDuplicateMessage,
  updateUser,
} from '../services/api';
import './Users.css';

function filterAssignableRoles(roles) {
  return roles.filter((role) => {
    const code = String(role?.code || '').toUpperCase();
    const name = String(role?.name || '').toLowerCase();
    return (
      code === 'FIELD_OFFICER' ||
      code === 'FO' ||
      code.includes('ADMIN') ||
      name.includes('field officer') ||
      name.includes('admin')
    );
  });
}

function getInitials(firstName, lastName, username) {
  const fromNames = `${firstName || ''} ${lastName || ''}`.trim();
  const text = fromNames || username || 'U';
  const parts = text.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function extractPrimaryProjectId(u) {
  if (!u) return '';
  if (Array.isArray(u.project_ids) && u.project_ids.length > 0) {
    return String(u.project_ids[0]);
  }
  const single = u.project?.id ?? u.project_id ?? u.project?.project_id;
  if (single != null && single !== '') return String(single);
  const arr = u.projects;
  if (Array.isArray(arr) && arr.length > 0) {
    const p = arr[0];
    return String(p?.id ?? p?.project_id ?? p?.projectId ?? '');
  }
  return '';
}

function normalizeUser(user) {
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || user.username || 'Unnamed User';
  const roleName = user.role?.name || user.role?.code || 'User';
  const isAdmin = String(roleName).toLowerCase().includes('admin');

  return {
    id: user.id,
    fullName,
    initials: getInitials(firstName, lastName, user.username),
    email: user.email || '—',
    phone: user.phone || user.alternate_phone || '—',
    roleName,
    isAdmin,
    status: user.is_active ? 'Active' : 'Inactive',
    department: user.department || (isAdmin ? 'Operations' : 'Field Operations'),
    projectAccess: user.project?.name || user.project_name || 'All',
  };
}

const emptyCreateForm = () => ({
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  project_id: '',
  role_id: '',
});

export default function Users() {
  const { user: authUser } = useAuth();
  const [sourceUsers, setSourceUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editProjectFallback, setEditProjectFallback] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [menuUserId, setMenuUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [actionFlash, setActionFlash] = useState({ tone: '', message: '' });

  const isEditMode = editingUserId != null;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const users = await fetchUsersForUsersPage();
      setSourceUsers(users);
      setRows(users.map(normalizeUser));
    } catch (err) {
      setError(humanizeError(err, 'Could not load users. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!actionFlash.message) return undefined;
    const timer = setTimeout(() => setActionFlash({ tone: '', message: '' }), 4500);
    return () => clearTimeout(timer);
  }, [actionFlash]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (menuUserId == null) return;
    const handlePointerDown = (e) => {
      if (e.target.closest(`[data-user-menu="${menuUserId}"]`)) return;
      setMenuUserId(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuUserId]);

  const handleDeleteUser = useCallback(
    async (rowId) => {
      if (!window.confirm('Delete this user? This cannot be undone.')) return;
      setMenuUserId(null);
      setDeletingUserId(rowId);
      try {
        await deleteUser(rowId);
        await loadUsers();
        setActionFlash({ tone: 'success', message: 'User deleted.' });
      } catch (err) {
        setActionFlash({
          tone: 'error',
          message: humanizeError(err, 'Could not delete the user. Please try again.'),
        });
      } finally {
        setDeletingUserId(null);
      }
    },
    [loadUsers]
  );

  const projectSelectOptions = useMemo(() => {
    const raw = authUser?.projects;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((p, i) => ({
        id: String(p.id ?? p.project_id ?? p.projectId ?? i),
        label: String(p.name ?? p.project_name ?? p.label ?? `Project ${p.id ?? i}`),
      }));
    }
    const envId = process.env.REACT_APP_PROJECT_ID;
    if (envId) {
      return [{ id: String(envId), label: 'Project' }];
    }
    return [];
  }, [authUser]);

  const mergedProjectOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of projectSelectOptions) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
    if (editProjectFallback && !seen.has(editProjectFallback.id)) {
      out.push(editProjectFallback);
    }
    return out;
  }, [projectSelectOptions, editProjectFallback]);

  const filteredRows = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return rows;
    return rows.filter((row) => {
      return (
        row.fullName.toLowerCase().includes(value) ||
        row.email.toLowerCase().includes(value) ||
        row.phone.toLowerCase().includes(value) ||
        row.roleName.toLowerCase().includes(value)
      );
    });
  }, [rows, query]);

  const resetCreateForm = useCallback(() => {
    setCreateForm(emptyCreateForm());
    setSubmitError('');
    setSubmitSuccess('');
  }, []);

  const openCreateModal = useCallback(async () => {
    setEditingUserId(null);
    setEditProjectFallback(null);
    resetCreateForm();
    setRolesError('');
    setShowCreateModal(true);
    setRolesLoading(true);
    try {
      const roles = await fetchAllRoles();
      const filtered = filterAssignableRoles(roles);
      setRoleOptions(filtered);
      setCreateForm((prev) => ({
        ...prev,
        ...(filtered.length === 1 ? { role_id: String(filtered[0].id) } : {}),
        ...(projectSelectOptions.length === 1 ? { project_id: projectSelectOptions[0].id } : {}),
      }));
    } catch (err) {
      setRolesError(humanizeError(err, 'Could not load roles. Please try again.'));
    } finally {
      setRolesLoading(false);
    }
  }, [resetCreateForm, projectSelectOptions]);

  const openEditModal = useCallback(
    async (userId) => {
      setMenuUserId(null);
      const u = sourceUsers.find((x) => Number(x.id) === Number(userId));
      if (!u) return;

      const pid = extractPrimaryProjectId(u);
      setEditingUserId(Number(userId));
      setSubmitError('');
      setSubmitSuccess('');
      setRolesError('');
      setEditProjectFallback(
        pid && !projectSelectOptions.some((p) => p.id === String(pid))
          ? {
              id: String(pid),
              label: u.project?.name || u.project_name || `Project ${pid}`,
            }
          : null
      );
      setShowCreateModal(true);
      setRolesLoading(true);
      try {
        const roles = await fetchAllRoles();
        const filtered = filterAssignableRoles(roles);
        const mergedRoles = [...filtered];
        const ridForMerge = Number(u.role_id ?? u.role?.id);
        if (Number.isFinite(ridForMerge) && ridForMerge > 0 && !mergedRoles.some((r) => Number(r.id) === ridForMerge)) {
          mergedRoles.push({
            id: ridForMerge,
            name: u.role?.name || `Role ${ridForMerge}`,
            code: u.role?.code,
          });
        }
        setRoleOptions(mergedRoles);
        const roleIdStr = u.role_id != null || u.role?.id != null ? String(u.role_id ?? u.role?.id) : '';
        setCreateForm({
          username: u.username || '',
          email: u.email || '',
          password: '',
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          phone: String(u.phone || u.alternate_phone || '').trim(),
          project_id: pid ? String(pid) : '',
          role_id: roleIdStr,
        });
      } catch (err) {
        setRolesError(humanizeError(err, 'Could not load roles. Please try again.'));
      } finally {
        setRolesLoading(false);
      }
    },
    [sourceUsers, projectSelectOptions]
  );

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setEditingUserId(null);
    setEditProjectFallback(null);
    setMenuUserId(null);
    resetCreateForm();
    setRoleOptions([]);
    setRolesError('');
  }, [resetCreateForm]);

  const handleSaveUser = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const roleId = Number(createForm.role_id);
      if (!roleId) {
        throw new Error('Select a role.');
      }
      const projectIdNum = Number(createForm.project_id);
      if (!Number.isInteger(projectIdNum) || projectIdNum <= 0) {
        throw new Error('Select a project.');
      }

      const body = {
        username: createForm.username.trim(),
        email: createForm.email.trim(),
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        phone: createForm.phone.trim(),
        role_id: roleId,
        project_ids: [projectIdNum],
      };

      if (isEditMode) {
        if (createForm.password.trim()) {
          body.password = createForm.password.trim();
        }
        await updateUser(editingUserId, body);
        setSubmitSuccess('User updated successfully.');
      } else {
        if (!createForm.password) {
          throw new Error('Password is required.');
        }
        await createUser({
          ...body,
          password: createForm.password,
        });
        setSubmitSuccess('User created successfully.');
      }

      await loadUsers();
      setTimeout(() => {
        closeCreateModal();
      }, 600);
    } catch (err) {
      const friendly = humanizeError(
        err,
        isEditMode
          ? 'Could not update the user. Please try again.'
          : 'Could not create the user. Please try again.',
      );
      setSubmitError(normalizeUserDuplicateMessage(friendly));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="users-state">Loading users...</div>;
  if (error) return <div className="users-state users-error">{error}</div>;

  return (
    <div className="users-page">
      <header className="users-page-header">
        <div className="users-heading">
          <div>
            <h2 className="users-page-title">Users</h2>
            <p className="users-page-subtitle">{rows.length} registered users</p>
          </div>
          <button type="button" className="users-add-btn" onClick={openCreateModal}>
            + Add User
          </button>
        </div>
        <div className="users-search-wrap">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users..."
            aria-label="Search users"
          />
        </div>
      </header>

      {actionFlash.message ? (
        <div className={`users-page-flash users-page-flash-${actionFlash.tone}`} role="status">
          {actionFlash.message}
          <button
            type="button"
            className="users-page-flash-close"
            onClick={() => setActionFlash({ tone: '', message: '' })}
            aria-label="Dismiss"
          >
            <FiX />
          </button>
        </div>
      ) : null}

      <div className="users-grid">
        {filteredRows.length === 0 ? (
          <div className="users-empty">No users found</div>
        ) : (
          filteredRows.map((row) => {
            const isSelf = authUser?.id != null && String(authUser.id) === String(row.id);
            return (
            <article className="user-card" key={row.id}>
              <div className="user-card-top">
                <div className="user-avatar">{row.initials}</div>
                <div className="user-name-wrap">
                  <h3>{row.fullName}</h3>
                  <span className="user-status">{row.status}</span>
                </div>
                <div className="user-card-menu-wrap" data-user-menu={row.id}>
                  <button
                    type="button"
                    className="user-menu-btn"
                    aria-expanded={menuUserId === row.id}
                    aria-haspopup="true"
                    aria-label="User actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuUserId((prev) => (prev === row.id ? null : row.id));
                    }}
                  >
                    <FiMoreVertical />
                  </button>
                  {menuUserId === row.id ? (
                    <div className="user-menu-dropdown" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className="user-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuUserId(null);
                          openEditModal(row.id);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="user-menu-item user-menu-item-danger"
                        disabled={isSelf || deletingUserId === row.id}
                        title={isSelf ? 'You cannot delete your own account' : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSelf) return;
                          handleDeleteUser(row.id);
                        }}
                      >
                        {deletingUserId === row.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <span className={`user-role-tag ${row.isAdmin ? 'admin' : 'fo'}`}>{row.roleName}</span>

              <div className="user-meta">
                <p>{row.email}</p>
                <p>{row.phone}</p>
              </div>

              <div className="user-extra">
                <p className="extra-label">DEPARTMENT</p>
                <p className="extra-value">{row.department}</p>
              </div>
              <div className="user-extra">
                <p className="extra-label">PROJECT ACCESS</p>
                <p className="extra-value">{row.projectAccess}</p>
              </div>
            </article>
            );
          })
        )}
      </div>

      {showCreateModal && (
        <div className="users-modal-backdrop" role="presentation" onClick={closeCreateModal}>
          <div className="users-modal" role="dialog" aria-modal="true" aria-labelledby="users-form-title" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <div>
                <h3 id="users-form-title">{isEditMode ? 'Edit User' : 'Add New User'}</h3>
                <p>
                  {isEditMode
                    ? 'Update account details, role, and project access'
                    : 'Create an account and assign role and project access'}
                </p>
              </div>
              <button type="button" className="users-modal-close" onClick={closeCreateModal} aria-label="Close">
                <FiX />
              </button>
            </div>

            {rolesLoading ? (
              <div className="users-modal-body users-modal-loading">Loading roles...</div>
            ) : rolesError ? (
              <div className="users-modal-body users-modal-loading users-error">{rolesError}</div>
            ) : roleOptions.length === 0 ? (
              <div className="users-modal-body users-modal-loading">No assignable roles found for this project.</div>
            ) : (
              <form className="users-modal-form" onSubmit={handleSaveUser}>
                <section className="users-modal-section">
                  <h4 className="users-modal-section-title">Basic Information</h4>
                  <div className="users-modal-fields-grid">
                    <label className="users-modal-label">
                      <span className="users-modal-label-text">
                        First Name <span className="users-required" aria-hidden="true">*</span>
                      </span>
                      <input
                        required
                        placeholder="Enter First Name"
                        value={createForm.first_name}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, first_name: e.target.value }))}
                      />
                    </label>
                    <label className="users-modal-label">
                      <span className="users-modal-label-text">
                        Last Name <span className="users-required" aria-hidden="true">*</span>
                      </span>
                      <input
                        required
                        placeholder="Enter Last Name"
                        value={createForm.last_name}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, last_name: e.target.value }))}
                      />
                    </label>
                    <label className="users-modal-label">
                      <span className="users-modal-label-text">
                        Email Address <span className="users-required" aria-hidden="true">*</span>
                      </span>
                      <input
                        type="email"
                        required
                        placeholder="ravikumar@example.com"
                        value={createForm.email}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </label>
                    <label className="users-modal-label">
                      <span className="users-modal-label-text">
                        Phone Number <span className="users-required" aria-hidden="true">*</span>
                      </span>
                      <input
                        required
                        placeholder="+91 98765 43210"
                        value={createForm.phone}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, phone: e.target.value }))}
                      />
                    </label>
                  </div>
                </section>

                <section className="users-modal-section">
                  <h4 className="users-modal-section-title">Role Selection</h4>
                  <label className="users-modal-label users-modal-field-full">
                    <span className="users-modal-label-text">
                      Select role <span className="users-required" aria-hidden="true">*</span>
                    </span>
                    <select
                      required
                      value={createForm.role_id}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, role_id: e.target.value }))}
                    >
                      <option value="">Select role</option>
                      {roleOptions.map((r) => (
                        <option key={r.id} value={String(r.id)}>
                          {r.name || r.code || `Role ${r.id}`}
                        </option>
                      ))}
                    </select>
                  </label>
                </section>

                <section className="users-modal-section">
                  <h4 className="users-modal-section-title">Credentials</h4>
                  <div className="users-modal-fields-grid">
                    <label className="users-modal-label users-modal-field-full">
                      <span className="users-modal-label-text">
                        Username <span className="users-required" aria-hidden="true">*</span>
                      </span>
                      <input
                        required
                        autoComplete="username"
                        placeholder="Enter Username"
                        value={createForm.username}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))}
                      />
                    </label>
                    <label className="users-modal-label users-modal-field-full">
                      <span className="users-modal-label-text">
                        Password{' '}
                        {!isEditMode ? <span className="users-required" aria-hidden="true">*</span> : null}
                        {isEditMode ? <span className="users-optional-hint"> (optional — leave blank to keep)</span> : null}
                      </span>
                      <input
                        type="password"
                        required={!isEditMode}
                        autoComplete="new-password"
                        placeholder={isEditMode ? 'Leave blank to keep current password' : 'Enter Password'}
                        value={createForm.password}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
                      />
                    </label>
                    <label className="users-modal-label users-modal-field-full">
                      <span className="users-modal-label-text">
                        Project <span className="users-required" aria-hidden="true">*</span>
                      </span>
                      <select
                        required
                        value={createForm.project_id}
                        disabled={mergedProjectOptions.length === 0}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, project_id: e.target.value }))}
                      >
                        <option value="">Select Project</option>
                        {mergedProjectOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      {mergedProjectOptions.length === 0 ? (
                        <span className="users-modal-hint">No projects on your account. Set REACT_APP_PROJECT_ID or log in with project access.</span>
                      ) : null}
                    </label>
                  </div>
                </section>

                {submitError ? <p className="users-submit-msg users-submit-error">{submitError}</p> : null}
                {submitSuccess ? <p className="users-submit-msg users-submit-success">{submitSuccess}</p> : null}

                <div className="users-modal-actions">
                  <button type="button" className="users-modal-secondary" onClick={closeCreateModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="users-add-btn users-modal-submit"
                    disabled={submitting || mergedProjectOptions.length === 0}
                  >
                    {submitting ? (isEditMode ? 'Saving...' : 'Adding...') : isEditMode ? 'Save changes' : 'Add User'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
